import { ArrowSmallRightIcon } from '@heroicons/react/24/solid'
import { debounce } from 'lodash'
import Button from 'app/components/Button'
import CircularProgressBar from 'app/components/CircularProgressBar'
import { ControlledSearch } from 'app/components/ui/controlled-search'
import { LazyLocationCell } from 'app/components/LazyLocationCell'
import PhoneNumber from 'app/components/PhoneNumber'
import Spinner from 'app/components/Spinner'
import { Toggle } from 'app/components/Toggle'
import { ContactNameIcon } from 'app/components/contacts/ContactNameIcon'
import PatientNameIcon from 'app/components/contacts/PatientNameIcon'
import SimpleTable from 'app/components/table/SimpleTable'
import type { UseMatchPatientModalReturn } from './useMatchPatientModal'

export type MatchPatientModalComponentProps = UseMatchPatientModalReturn

export function MatchPatientModalComponent(props: MatchPatientModalComponentProps) {
  const {
    patient,
    contact,
    fetcher,
    data,
    columns,
    matchedContact,
    selectedContact,
    showConfirmationStep,
    showNewContactStep,
    inProgress,
    progress,
    isLoading,
    searchWorkspace,
    setPage,
    setShowConfirmationStep,
    setShowNewContactStep,
    onSearchValueChange,
    onSearchWorkspaceToggle,
    matchPatient,
    createNewContact,
    goToContact,
    done,
  } = props

  return (
    <div id="match-patient-modal-link" className="flex h-full w-full flex-col">
      <div className="flex items-center justify-between bg-surface-subtle px-8 pb-8 pt-12">
        <div className="relative grow space-y-4 rounded-lg border border-subtle bg-surface p-4 shadow-elevation-2">
          <div className="flex items-center space-x-2">
            <PatientNameIcon patient={patient} heightClassName={`h-10`} widthClassname={`w-10`} />
            <div>
              <p className="text-heading-sm text-strong">{`${patient.firstName} ${patient.lastName}`}</p>
              <p className="text-body-xs text-subtle">
                <LazyLocationCell locationId={patient?.locationId!} />
              </p>
              <div className="text-body-xs text-subtle">
                <PhoneNumber number={patient?.cellPhone ?? patient?.homePhone} />
              </div>
            </div>
          </div>
          <p className="absolute -top-8 left-0 mb-2 text-ui-sm">Link Patient</p>
        </div>
        {!showNewContactStep && (
          <div className="mx-8 flex h-10 w-10 items-center justify-center rounded-full border border-default bg-surface shadow-elevation-1">
            <ArrowSmallRightIcon className="h-4 w-4 text-subtle" />
          </div>
        )}
        {(selectedContact || matchedContact) && !showNewContactStep && (
          <div className="relative grow space-y-4 rounded-lg border border-subtle bg-surface p-4 shadow-elevation-2">
            <div className="flex items-center space-x-2">
              <ContactNameIcon
                contact={selectedContact ?? matchedContact!}
                heightClassName={`h-10`}
                widthClassName={`w-10`}
              />
              <div>
                <p className="text-heading-sm text-strong">{selectedContact?.name ?? matchedContact?.name}</p>
                <p className="text-body-xs text-subtle">
                  <LazyLocationCell locationId={selectedContact?.locationId! ?? matchedContact?.locationId!} />
                </p>
                <div className="text-body-xs text-subtle">
                  <PhoneNumber
                    number={
                      selectedContact
                        ? (selectedContact?.cellPhone ?? selectedContact?.homePhone)
                        : (matchedContact?.cellPhone ?? matchedContact?.homePhone)
                    }
                  />
                </div>
              </div>
            </div>
            <p className="absolute -top-8 left-0 mb-2 text-ui-sm">With Contact</p>
          </div>
        )}
      </div>
      {!showConfirmationStep && !showNewContactStep && (
        <>
          <div className="bg-surface-subtle px-4 pb-6 pt-2">
            <div className="mx-4 flex justify-between gap-2">
              <div>
                <ControlledSearch onValueChange={onSearchValueChange} />
              </div>
              <Toggle label="Search Workspace" defaultValue={searchWorkspace} handleToggle={onSearchWorkspaceToggle} />
            </div>
          </div>
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Spinner size={20} />
            </div>
          ) : (
            <div className="flex flex-col overflow-hidden">
              <SimpleTable
                data={data}
                columns={columns}
                onScroll={debounce((e) => {
                  const target = e.target as HTMLDivElement
                  if (
                    fetcher.state === `idle` &&
                    target.scrollTop > 0 &&
                    target.scrollTop + target.offsetHeight >= target.scrollHeight * 0.8
                  ) {
                    setPage((page) => page + 1)
                  }
                }, 1000)}
                hideFooter
              />
            </div>
          )}
        </>
      )}
      {showConfirmationStep && !inProgress && (
        <div className="flex grow items-center justify-center">
          <div className="p-4">
            <p className="text-body-md">
              <span className="text-heading-sm">{`${patient.firstName} ${patient.lastName}`}</span> will be matched with
              {` `}
              <span className="text-heading-sm">{selectedContact?.name}</span>
            </p>
            <div className="ml-2 mt-4 flex flex-col space-y-4 text-body-sm text-subtle">
              <p>1. The contact will inherit properties from the patient where applicable.</p>
              <p>2. Using the new data from the patient, the contact will be rebuilt.</p>
              <p>3. This may result in a change in status, dates and a variety of existing contact fields.</p>
              <p>4. Please make sure the contact you are linking the patient to is the correct contact.</p>
            </div>
          </div>
        </div>
      )}
      {showConfirmationStep && inProgress && (
        <div className="mx-auto my-4 flex grow flex-col items-center justify-center space-y-4">
          <CircularProgressBar progress={progress} />
          <p className="text-heading-md">{progress !== 100 ? `Matching in progress...` : `Successfully matched`}</p>
        </div>
      )}
      {showNewContactStep && !inProgress && (
        <div className="flex grow items-center justify-center">
          <div className="p-4">
            <p className="text-body-md">
              <span className="text-heading-sm">Create a new contact</span> from the{` `}
              <span className="text-heading-sm">Patient</span>
            </p>
            <div className="ml-2 mt-4 flex flex-col space-y-4 text-body-sm text-subtle">
              <p>1. The contact created will be based on the data from the patient.</p>
              <p>2. Logic will run to set date fields, lifecycle, and lead status.</p>
              <p>3. This may result in the contact being tagged and included in lists like "Needs Action".</p>
              <p>4. Please make sure a contact for this patient doesn't already exist.</p>
            </div>
          </div>
        </div>
      )}
      {showNewContactStep && inProgress && (
        <div className="mx-auto my-4 flex grow flex-col items-center justify-center space-y-4">
          <CircularProgressBar progress={progress} />
          <p className="text-heading-md">{progress !== 100 ? `Creating contact...` : `Contact created`}</p>
        </div>
      )}
      <div className="mt-auto flex justify-end space-x-2 border-t border-default px-6 py-3">
        {!showConfirmationStep && !showNewContactStep ? (
          <div className="flex grow items-center justify-between">
            <Button label="Create new contact" mode="secondary" size="md" onClick={() => setShowNewContactStep(true)} />
            <div className="flex space-x-2">
              <Button label="Cancel" mode="secondary" size="md" onClick={() => done()} />
              <Button
                label="Next"
                mode="primary"
                size="md"
                disabled={!selectedContact || selectedContact.id === contact.id ? true : undefined}
                onClick={() => setShowConfirmationStep(true)}
              />
            </div>
          </div>
        ) : !inProgress ? (
          <div className="flex grow items-center justify-between">
            <p className="text-heading-xs text-red-500">This action can't be undone</p>
            <div className="flex space-x-2">
              <Button
                label="Back"
                mode="secondary"
                size="md"
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setShowConfirmationStep(false)
                  setShowNewContactStep(false)
                }}
              />
              <Button
                label="Confirm"
                mode="primary"
                size="md"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  // eslint-disable-next-line @typescript-eslint/no-floating-promises
                  showConfirmationStep ? matchPatient() : createNewContact()
                }}
              />
            </div>
          </div>
        ) : (
          <Button
            label="Go to Contact"
            mode="primary"
            size="md"
            disabled={progress !== 100 ? true : undefined}
            onClick={() => goToContact()}
          />
        )}
      </div>
    </div>
  )
}
